import {
    Component,
    ViewEncapsulation,
    OnInit,
    inject,
    Input,
    Output,
    EventEmitter,
} from "@angular/core";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/assetmanagement/i18n/scope";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { CerrixInstructionsIconComponent } from "@app/shared/cerrix-instructions-icon/cerrix-instructions-icon.component";
import { AssetManagementModel } from "@models/assetmanagement/assetmanagementmodel";
import { CommonModule } from "@angular/common";
import { CerrixTreeItemPickerMultipleComponent } from "@app/shared/organisms/cerrix-tree-item-picker-multiple/cerrix-tree-item-picker-multiple.component";
import { CerrixUserTypePickerComponent } from "@app/shared/organisms/cerrix-user-type-picker/cerrix-user-type-picker.component";
import { CerrixUserTypePickerConfig } from "@app/shared/organisms/cerrix-user-type-picker/models/cerrix-user-type-picker-config";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { HttpClient } from "@angular/common/http";
import { finalize, firstValueFrom } from "rxjs";
import { ToastrService } from "ngx-toastr";
import {
    AssetManagementService,
    AssetProjection,
} from "@app/assetmanagement/services/assetmanagement-data.service";
import {
    CERRIX_SLIDE_OUT_EDIT_ITEM_SECTION,
    CerrixSlideOutEditItemSection,
} from "@app/shared/organisms/cerrix-slide-out-edit-item/models/cerrix-slide-out-edit-item-section.model";
import { IdGuidNameCombination } from "@models/generic/IdGuidNameCombination";
import { ProcessInformationModel } from "@models/assetmanagement/processinformationmodel";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { ModuleType } from "@enums/ModuleType";

@Component({
    selector: "app-edit-asset-process-information",
    templateUrl: "./edit-asset-process-information.component.html",
    imports: [
        CommonModule,
        TranslatePipe,
        CerrixInstructionsIconComponent,
        CerrixButtonComponent,
        CerrixTreeItemPickerMultipleComponent,
        CerrixUserTypePickerComponent,
    ],
    viewProviders: [provideTranslationScope(scope)],
    providers: [
        {
            provide: CERRIX_SLIDE_OUT_EDIT_ITEM_SECTION,
            useExisting: EditAssetProcessInformationComponent,
        },
    ],
    encapsulation: ViewEncapsulation.None,
})
export class EditAssetProcessInformationComponent
    implements OnInit, CerrixSlideOutEditItemSection
{
    @Input() model: AssetManagementModel;

    @Output() modelChange: EventEmitter<AssetManagementModel> =
        new EventEmitter<AssetManagementModel>();
    @Output() closeEdit: EventEmitter<void> = new EventEmitter<void>();

    protected editModel: ProcessInformationModel;
    protected organizationsList: CerrixTreeItem[] = [];
    protected businessDimensionsList: CerrixTreeItem[] = [];
    protected userTypes: CerrixUserTypePickerConfig[] = [];
    protected userTypeValue: Record<string, string[]> = {};

    protected editSaving = false;

    // Saved business dimensions that still are not in the tree (the server returns the saved
    // ones even when restricted). They stay on the asset when saving, because the user cannot
    // have meant to remove what they cannot see.
    private hiddenBusinessDimensions: string[] = [];

    private _ds = inject(AssetManagementService);
    private _promptService = inject(CerrixPromptService);
    private _translation = injectTranslation(scope);
    private _toastService = inject(ToastrService);
    private _standingDataDS = inject(StandingdataDataService);
    private _http = inject(HttpClient);

    async ngOnInit() {
        // Before the await: the slide-out can call hasUnsavedChanges() while the scope loads.
        this.revertChanges();

        // t() needs the scope loaded first; before ready() it throws in development.
        await this._translation.ready();

        const saved = this.savedProcessInformation();

        // The selected ids are passed along so a value the user cannot normally see is still shown.
        const [organizations, businessDimensions, users] = await Promise.all([
            firstValueFrom(
                this._standingDataDS.getOrganizationsIncludingSelected(
                    ModuleType.AssetManagement,
                    saved.organizationsInUse,
                    false,
                ),
            ),
            firstValueFrom(
                this._standingDataDS.getUserViewableBusinessDimensions(
                    ModuleType.AssetManagement,
                    saved.businessDimensions,
                ),
            ),
            this.getUsers([...saved.responsibles, ...saved.informed]),
        ]);

        this.organizationsList = organizations;
        this.businessDimensionsList = businessDimensions;
        this.userTypes = [
            {
                key: "responsible",
                label: this._translation.t(
                    "edit-process-information.responsiblesLabel",
                ),
                description: this._translation.t(
                    "edit-process-information.responsiblesInfo",
                ),
                users,
                multiselect: true,
            },
            {
                key: "informed",
                label: this._translation.t(
                    "edit-process-information.informedLabel",
                ),
                description: this._translation.t(
                    "edit-process-information.informedInfo",
                ),
                users,
                multiselect: true,
            },
        ];

        const visibleBusinessDimensions = new Set(this.collectGuids(businessDimensions));
        this.hiddenBusinessDimensions = saved.businessDimensions.filter(
            (id) => !visibleBusinessDimensions.has(id),
        );

        this.revertChanges();
    }

    protected onUserTypeValueChange(value: Record<string, string[]>): void {
        this.userTypeValue = value;
        this.editModel.responsibles = value["responsible"] ?? [];
        this.editModel.informed = value["informed"] ?? [];
    }

    public async save(): Promise<boolean> {
        this.editSaving = true;
        const savingPrompt = this._promptService.loader(
            this._translation.t("detail.savingChanges"),
        );

        const storedAsset = await firstValueFrom(
            this.saveSection().pipe(
                finalize(() => {
                    savingPrompt.close();
                    this.editSaving = false;
                }),
            ),
        );

        if (storedAsset?.guid) {
            this.model = Object.assign(new AssetManagementModel(), storedAsset, {
                processInformation: this.toModel(storedAsset),
            });
            this.modelChange.emit(this.model);
            this.revertChanges();

            this._toastService.success(
                this._translation.t("detail.updateCompleted"),
            );
            return true;
        }
        return false;
    }

    private async getUsers(selected: string[]): Promise<IdGuidNameCombination[]> {
        const params = selected.length ? `?selected=${selected.join(",")}` : "";

        return await firstValueFrom(
            this._http.get<IdGuidNameCombination[]>(`/api/users/list` + params),
        );
    }

    private collectGuids(items: CerrixTreeItem[]): string[] {
        return (items ?? []).flatMap((item) => [
            item.Guid.toString(),
            ...this.collectGuids(item.Children),
        ]);
    }

    private savedProcessInformation(): ProcessInformationModel {
        return Object.assign(new ProcessInformationModel(), this.model.processInformation);
    }

    private toModel(asset: AssetProjection): ProcessInformationModel {
        return Object.assign(new ProcessInformationModel(), asset.processInformation);
    }

    public hasUnsavedChanges(): boolean {
        const saved = this.visibleSavedProcessInformation();

        return (
            !this.sameIds(saved.responsibles, this.editModel.responsibles) ||
            !this.sameIds(saved.informed, this.editModel.informed) ||
            !this.sameIds(saved.organizationsInUse, this.editModel.organizationsInUse) ||
            !this.sameIds(saved.businessDimensions, this.editModel.businessDimensions)
        );
    }

    private sameIds(left: string[], right: string[]): boolean {
        return left.length === right.length && left.every((id) => right.includes(id));
    }

    private saveSection() {
        return this._ds.vbetaUpdateProcessInformation(this.model.guid, {
            responsibles: this.editModel.responsibles,
            informed: this.editModel.informed,
            organizationsInUse: this.editModel.organizationsInUse,
            businessDimensions: [...this.editModel.businessDimensions, ...this.hiddenBusinessDimensions],
            version: this.model.version,
        });
    }

    private visibleSavedProcessInformation(): ProcessInformationModel {
        const saved = this.savedProcessInformation();
        saved.businessDimensions = saved.businessDimensions.filter(
            (id) => !this.hiddenBusinessDimensions.includes(id),
        );
        return saved;
    }

    private revertChanges(): void {
        const saved = this.visibleSavedProcessInformation();
        this.editModel = {
            responsibles: [...saved.responsibles],
            informed: [...saved.informed],
            organizationsInUse: [...saved.organizationsInUse],
            businessDimensions: [...saved.businessDimensions],
        };
        this.userTypeValue = {
            responsible: this.editModel.responsibles,
            informed: this.editModel.informed,
        };
    }
}
